import { usePermissionValues, usePermissionsLoadedSelector } from '../../redux/selectors/content';
import { useEffect, useState, useRef } from 'react';
import { buildAuthUrl } from '../../utils'
import { useNavigate, useLocation } from 'react-router-dom'
import { dispatchNotificationState } from '../../redux/actions/common';

interface IProtectedRoute {
  isSignedIn: boolean
  children: JSX.Element
  havePermission?: boolean;
}

const ProtectedRoute = ({ isSignedIn, children, havePermission }: IProtectedRoute) => {

  const location = useLocation();
  const permissionValues: any = [...usePermissionValues(), 'view_tempEditor'];
  const permissionsLoaded = usePermissionsLoadedSelector();
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false); // Track if navigation has already been triggered

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const url: any = location.pathname.split('/'); // Get the last part of the URL
  const base = url[1] || 'home';  // "past_tickets"

  const checkPermission = (url: any) => {
    // Special case for myworkspace route - check for view_enterprisehub permission
    if (url === 'myworkspace') {
      return permissionValues.includes('view_enterprisehub');
    }
    const formattedUrl = `view_${url}`;
    return permissionValues.includes(formattedUrl);
  };

  useEffect(() => {
    // Check permissions when the component mounts
    setHasPermission(checkPermission(base));
  }, [permissionValues, base]);

  // Handle permission denial - run only once per URL
  useEffect(() => {
    if (hasPermission === false && !hasNavigatedRef.current && isSignedIn && permissionsLoaded) {
      hasNavigatedRef.current = true; // Mark as navigated for this URL

      // Show info notification before navigating
      dispatchNotificationState({
        message: 'Permission Denied',
        description: 'You don\'t have permission to view this page.',
        type: 'info',
      });

      // Navigate to previous page after a short delay to allow notification to show
      setTimeout(() => {
        console.log("Redirecting to previous page...");
        navigate(-1);
      }, 2000);
    }
  }, [hasPermission, navigate, location.pathname, permissionsLoaded]);

  // Reset navigation flag when permission becomes true
  useEffect(() => {
    if (hasPermission === true) {
      hasNavigatedRef.current = false;
    }
  }, [hasPermission]);

  if (!isSignedIn) {
    // id_token in localStorage means handleRegularToken already ran but React state hasn't caught up yet
    // code in URL means PKCE exchange is actively in flight
    if (
      localStorage.getItem('id_token') ||
      new URLSearchParams(window.location.search).get('code')
    ) {
      return null
    }
    // pkce_in_progress without a code in the URL is stale (e.g. user navigated
    // back after a logout→B2C redirect). Clear it so the redirect fires normally.
    if (sessionStorage.getItem('pkce_in_progress')) {
      sessionStorage.removeItem('pkce_in_progress')
    }
    ;(async () => {
      const authUrl = await buildAuthUrl()
      console.log("Redirecting to login...", authUrl);
      localStorage.setItem("authRedirect", authUrl);
      window.location.href = authUrl;
    })()
    return null;
  }

  if (hasPermission === null || !permissionsLoaded) {
    return null;
  }

  if (!hasPermission) {
    return null; // Return null while navigating
  }
  return children
}

export default ProtectedRoute
